import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { usePrivacy } from '../../lib/privacy';
import { MainLayout } from '../../components/MainLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Building2, RefreshCw, ChevronDown, ChevronRight, FileText, Search,
  Copy, CalendarClock, Sparkles, Boxes, FileStack, X,
} from 'lucide-react';
import api from '../../lib/api';

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => Number(n || 0).toLocaleString();
const SS_KEY = 'paas_accounts_ui';

const Tile = ({ icon: Icon, label, value, tone = 'slate', testid }) => (
  <Card className="p-4 rounded-none border-l-4" style={{ borderLeftColor: `var(--paas-${tone})` }} data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <Icon className="h-4 w-4 text-muted-foreground" />
    </div>
    <div className="mt-2 text-2xl font-bold tabular-nums">{value}</div>
  </Card>
);

export default function PaasAccounts() {
  const navigate = useNavigate();
  const privacy = usePrivacy();
  const [currentAccount] = useState(() => {
    try { return JSON.parse(localStorage.getItem('current_account') || 'null'); } catch { return null; }
  });
  const accountId = currentAccount?.id;

  const saved = (() => { try { return JSON.parse(sessionStorage.getItem(SS_KEY) || '{}'); } catch { return {}; } })();
  const [qAccount, setQAccount] = useState(saved.qAccount || '');
  const [vendor, setVendor] = useState(saved.vendor || 'all');
  const [issueMonth, setIssueMonth] = useState(saved.issueMonth || 'all');
  const [expanded, setExpanded] = useState(saved.expanded || null);
  const [billsCache, setBillsCache] = useState({});

  const [data, setData] = useState({ items: [], vendors: [], issue_months: [], total: 0 });
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const restoredScroll = useRef(false);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ account_id: accountId, limit: '200' });
      if (qAccount) params.set('q_account', qAccount);
      if (vendor !== 'all') params.set('vendor', vendor);
      if (issueMonth !== 'all') params.set('issue_month', issueMonth);
      const [res, sm] = await Promise.all([
        api.get(`/paas/accounts?${params.toString()}`),
        api.get(`/paas/accounts/summary?account_id=${accountId}`),
      ]);
      setData(res.data);
      setSummary(sm.data);
    } catch (e) {
      toast.error('Failed to load Accounts');
    } finally {
      setLoading(false);
    }
  }, [accountId, qAccount, vendor, issueMonth]);

  useEffect(() => { load(); }, [load]);

  // Persist UI state for back-nav restoration.
  useEffect(() => {
    sessionStorage.setItem(SS_KEY, JSON.stringify({ qAccount, vendor, issueMonth, expanded }));
  }, [qAccount, vendor, issueMonth, expanded]);

  // Restore scroll once after data loads.
  useEffect(() => {
    if (!loading && !restoredScroll.current && saved.scrollY) {
      window.scrollTo(0, saved.scrollY);
      restoredScroll.current = true;
    }
  }, [loading, saved.scrollY]);

  const toggleExpand = async (gk) => {
    if (expanded === gk) { setExpanded(null); return; }
    setExpanded(gk);
    if (!billsCache[gk]) {
      try {
        const res = await api.get(`/paas/accounts/${gk}/bills?account_id=${accountId}`);
        setBillsCache((p) => ({ ...p, [gk]: res.data.bills || [] }));
      } catch { toast.error('Failed to load bills'); }
    }
  };

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const res = await api.post(`/paas/accounts/rebuild?account_id=${accountId}`);
      toast.success(`Regrouped ${res.data.accounts} accounts from ${res.data.bills} bills`);
      setBillsCache({});
      await load();
    } catch { toast.error('Rebuild failed'); }
    finally { setRebuilding(false); }
  };

  const openBill = (documentId) => {
    sessionStorage.setItem(SS_KEY, JSON.stringify({ qAccount, vendor, issueMonth, expanded, scrollY: window.scrollY }));
    navigate(`/viewer/${documentId}`, { state: { from: '/paas/accounts' } });
  };

  const clearFilters = () => { setQAccount(''); setVendor('all'); setIssueMonth('all'); };
  const hasFilters = qAccount || vendor !== 'all' || issueMonth !== 'all';

  return (
    <MainLayout>
      <style>{`:root{--paas-slate:#475569;--paas-green:#059669;--paas-amber:#d97706;--paas-red:#dc2626;--paas-blue:#2563eb}`}</style>
      <div className="space-y-6" data-testid="paas-accounts">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-amber-600" />
              <h1 className="text-2xl font-bold">Accounts</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Parsed bills auto-grouped by account, vendor & property {currentAccount?.name ? `· ${currentAccount.name}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none" onClick={rebuild} disabled={rebuilding} data-testid="accounts-rebuild">
              <RefreshCw className={`h-4 w-4 mr-2 ${rebuilding ? 'animate-spin' : ''}`} /> Regroup
            </Button>
            <Button className="rounded-none bg-amber-600 hover:bg-amber-700" onClick={() => navigate('/paas/onboard')} data-testid="accounts-onboard">
              <Sparkles className="h-4 w-4 mr-2" /> Onboard
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Tile icon={Building2} label="Accounts" value={num(summary.accounts)} tone="blue" testid="tile-accounts" />
          <Tile icon={FileStack} label="Bills" value={num(summary.bills)} tone="slate" testid="tile-bills" />
          <Tile icon={Copy} label="Duplicates" value={num(summary.duplicates)} tone="red" testid="tile-duplicates" />
          <Tile icon={CalendarClock} label="Overlaps" value={num(summary.overlaps)} tone="amber" testid="tile-overlaps" />
          <Tile icon={Boxes} label="Vendors" value={num(summary.vendors)} tone="green" testid="tile-vendors" />
        </div>

        <Card className="p-4 rounded-none">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground">Account no.</label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input className="rounded-none pl-8" placeholder="Search account number"
                  value={qAccount} onChange={(e) => setQAccount(e.target.value)} data-testid="filter-account" />
              </div>
            </div>
            <div className="min-w-[200px]">
              <label className="text-xs text-muted-foreground">Vendor</label>
              <Select value={vendor} onValueChange={setVendor}>
                <SelectTrigger className="rounded-none" data-testid="filter-vendor"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All vendors</SelectItem>
                  {(data.vendors || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px]">
              <label className="text-xs text-muted-foreground">Issue month</label>
              <Select value={issueMonth} onValueChange={setIssueMonth}>
                <SelectTrigger className="rounded-none" data-testid="filter-month"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All months</SelectItem>
                  {(data.issue_months || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {hasFilters && (
              <Button variant="ghost" className="rounded-none" onClick={clearFilters} data-testid="filter-clear">
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
            )}
          </div>
        </Card>

        <Card className="rounded-none overflow-hidden">
          <div className="px-4 py-3 border-b text-sm text-muted-foreground">
            {loading ? 'Loading…' : `${num(data.total)} account${data.total === 1 ? '' : 's'}`}
          </div>
          {loading ? (
            <div className="p-4 space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : data.items.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground" data-testid="accounts-empty">No accounts match your filters.</div>
          ) : (
            <div className="divide-y" data-testid="accounts-list">
              {data.items.map((a) => (
                <div key={a.group_key} data-testid={`account-row-${a.group_key}`}>
                  <div className="grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-muted/40 cursor-pointer"
                    onClick={() => toggleExpand(a.group_key)}>
                    <div className="col-span-3 min-w-0">
                      <div className="font-medium truncate">{privacy.name(a.property_name) || '—'}</div>
                      <div className="text-xs text-muted-foreground truncate">{privacy.name(a.customer_name)}</div>
                    </div>
                    <div className="col-span-2 text-sm tabular-nums truncate">{a.account_number || '—'}</div>
                    <div className="col-span-3 text-sm text-muted-foreground truncate">
                      {privacy.maskAddresses
                        ? privacy.address(`${a.address || ''}${a.city ? `, ${a.city}` : ''} ${a.state || ''}`.trim() || '—')
                        : <>{a.address || '—'}{a.city ? `, ${a.city}` : ''} {a.state}</>}
                    </div>
                    <div className="col-span-1 text-sm truncate">{a.vendor_name}</div>
                    <div className="col-span-1 text-xs text-muted-foreground tabular-nums">{a.last_bill_date || '—'}</div>
                    <div className="col-span-2 flex items-center justify-end gap-2">
                      {a.duplicate_count > 0 && <Badge variant="destructive" className="rounded-none text-[10px]" data-testid={`badge-dup-${a.group_key}`}>{a.duplicate_count} dup</Badge>}
                      {a.overlap_count > 0 && <Badge className="rounded-none text-[10px] bg-amber-500" data-testid={`badge-ov-${a.group_key}`}>{a.overlap_count} overlap</Badge>}
                      <Button variant="outline" size="sm" className="rounded-none" data-testid={`btn-bills-${a.group_key}`}>
                        {expanded === a.group_key ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        <span className="ml-1">{a.bill_count} bills</span>
                      </Button>
                    </div>
                  </div>

                  {expanded === a.group_key && (
                    <div className="bg-muted/30 px-4 py-3" data-testid={`accordion-${a.group_key}`}>
                      <div className="grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wide text-muted-foreground px-2 pb-2">
                        <div className="col-span-2">Issue date</div>
                        <div className="col-span-3">Period</div>
                        <div className="col-span-2">Commodity</div>
                        <div className="col-span-2 text-right">Amount Due</div>
                        <div className="col-span-2">Due date</div>
                        <div className="col-span-1 text-right">Bill</div>
                      </div>
                      {(billsCache[a.group_key] || []).map((b, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center px-2 py-1.5 text-sm border-t border-border/50" data-testid={`bill-${a.group_key}-${i}`}>
                          <div className="col-span-2 tabular-nums">{b.issue_date || '—'}</div>
                          <div className="col-span-3 text-xs text-muted-foreground tabular-nums">{b.period_from || '—'} → {b.period_to || '—'}</div>
                          <div className="col-span-2">{b.commodity || '—'}</div>
                          <div className="col-span-2 text-right tabular-nums">{money(b.total_amount_due)}</div>
                          <div className="col-span-2 tabular-nums">{b.due_date || '—'}
                            {b.is_duplicate && <Badge variant="destructive" className="ml-1 rounded-none text-[9px]">dup</Badge>}
                            {b.is_overlap && <Badge className="ml-1 rounded-none text-[9px] bg-amber-500">ov</Badge>}
                          </div>
                          <div className="col-span-1 text-right">
                            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-none"
                              onClick={(e) => { e.stopPropagation(); openBill(b.document_id); }}
                              data-testid={`view-bill-${a.group_key}-${i}`}>
                              <FileText className="h-4 w-4 text-amber-600" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {!billsCache[a.group_key] && <div className="px-2 py-2 text-sm text-muted-foreground">Loading bills…</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </MainLayout>
  );
}
